import React from 'react';
import { Spin } from 'antd';
import { cx, type Styleable } from '../../variants';

export interface SpinnerProps extends Styleable {
    /** 16 inline · 20 in a button · 32 in a card. */
    size?: 'inline' | 'button' | 'card';
    /**
     * Accompanying text. The sheet requires this on full-screen states — a
     * bare spinner tells the user nothing about what is happening or how long
     * it might take.
     */
    label?: React.ReactNode;
}

const PIXEL_SIZE: Record<NonNullable<SpinnerProps['size']>, number> = {
    inline: 16,
    button: 20,
    card: 32,
};

/**
 * EAA-1804: the sheet's spinner, at its three fixed sizes.
 *
 * Wraps antd's `Spin` (whose colour already comes from `createAntdTheme`)
 * mainly to pin the size scale — antd's `small | default | large` does not map
 * onto 16/20/32, and call sites were picking arbitrary pixel values.
 *
 * `label` is a first-class prop rather than an optional extra because the
 * sheet treats "never a bare spinner" as a rule for full-screen states.
 */
export function Spinner({
    size = 'inline',
    label,
    className,
    style,
}: SpinnerProps): React.ReactElement {
    const spin = <Spin size={size === 'card' ? 'large' : 'small'} />;

    if (!label) {
        return (
            <span
                className={cx(className)}
                style={{
                    display: 'inline-flex',
                    width: PIXEL_SIZE[size],
                    height: PIXEL_SIZE[size],
                    ...style,
                }}
            >
                {spin}
            </span>
        );
    }

    return (
        <div role="status" aria-live="polite" className={cx(className)} style={style}>
            <Spin size={size === 'card' ? 'large' : 'small'} description={label}>
                <span />
            </Spin>
        </div>
    );
}

export default Spinner;
